import React, { useState, useEffect, useCallback } from 'react';
import { homepageAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorBoundary from '../components/ErrorBoundary';
import WingbeatLogo from '../components/WingbeatLogo';
import WeatherStation from '../components/WeatherStation';
import RecentFlightLogs from '../components/RecentFlightLogs';
import { FEATURED_STATION } from '../config/weatherStations';
import './HomePage.css';

// A short plain-text teaser for the homepage news panel: prefer the stored
// excerpt, else the first paragraph of the article, stripped of HTML/shortcodes
// and truncated.
function newsTeaser(news) {
  if (!news) return '';
  if (news.excerpt) return news.excerpt;
  const html = news.introtext || news.content || '';
  // first paragraph, or whole thing if no <p>
  const firstPara = (html.match(/<p[^>]*>(.*?)<\/p>/is) || [null, html])[1] || html;
  const text = firstPara
    .replace(/\{[^}]*\}/g, '')      // drop {gallery}/{source} shortcodes
    .replace(/<[^>]+>/g, '')        // strip tags
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > 220 ? `${text.slice(0, 219).trimEnd()}…` : text;
}

function HomePage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);

  const loadContent = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await homepageAPI.getContent();
      
      if (response.success && response.data) {
        setContent(response.data);
        setRetryCount(0); // Reset retry count on success
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading homepage content:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // `loading` already starts true on mount, so run the fetch through an
    // awaited IIFE rather than calling loadContent() synchronously (which would
    // set state within the effect). loadContent remains for the retry button.
    let ignore = false;
    ;(async () => {
      try {
        await loadContent();
      } catch (err) {
        if (!ignore) console.error('Error loading homepage content:', err);
      }
    })();
    return () => { ignore = true; };
  }, [loadContent]);

  // Hero banner rotation effect
  useEffect(() => {
    if (content?.heroImages && content.heroImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentHeroIndex(prev => (prev + 1) % content.heroImages.length);
      }, 5000); // Change image every 5 seconds
      
      return () => clearInterval(interval);
    }
  }, [content?.heroImages]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    loadContent();
  };

  if (loading) {
    return (
      <div className="homepage">
        <LoadingSpinner message="Loading homepage content..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="homepage">
        <div className="error-container">
          <h2>Unable to Load Content</h2>
          <p className="error-message">{error}</p>
          <div className="error-actions">
            <button onClick={handleRetry} className="btn-primary">
              {retryCount > 0 ? `Retry (${retryCount})` : 'Retry'}
            </button>
            <button onClick={() => window.location.reload()} className="btn-secondary">
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatContent = (text) => {
    if (!text) return '';
    return text.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return '';
    }
  };

  return (
    <ErrorBoundary>
      <div className="homepage">
        {/* Hero Banner */}
        <header className="hero-banner">
          {content?.heroImages && content.heroImages.length > 0 ? (
            <div className="hero-slideshow">
              <div 
                className="hero-image"
                style={{
                  backgroundImage: `url(${content.heroImages[currentHeroIndex]?.imageUrl})`,
                }}
              >
                <div className="hero-overlay">
                  <div className="hero-content">
                    <WingbeatLogo className="hero-logo" height={200} title="Wingbeat Paragliding Club" />
                    <p>Scottish Borders Paragliding Community</p>
                    {content.heroImages[currentHeroIndex]?.caption && (
                      <p className="hero-caption">{content.heroImages[currentHeroIndex].caption}</p>
                    )}
                  </div>
                </div>
              </div>
              {content.heroImages.length > 1 && (
                <div className="hero-indicators">
                  {content.heroImages.map((_, index) => (
                    <button
                      key={index}
                      className={`hero-indicator ${index === currentHeroIndex ? 'active' : ''}`}
                      onClick={() => setCurrentHeroIndex(index)}
                      aria-label={`Show image ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="hero-fallback">
              <div className="hero-content">
                <WingbeatLogo className="hero-logo" height={200} title="Wingbeat Paragliding Club" />
                <p>Scottish Borders Paragliding Community</p>
              </div>
            </div>
          )}
        </header>

        {/* Seasonal Message */}
        {content?.seasonalMessage && (
          <section className="seasonal-message">
            <div className={`seasonal-alert seasonal-${content.seasonalMessage.type}`}>
              <h2>{content.seasonalMessage.title}</h2>
              <div className="seasonal-content">
                {formatContent(content.seasonalMessage.content)}
              </div>
            </div>
          </section>
        )}

        {/* Club History */}
        {content?.clubHistory && (
          <section className="club-history-section">
            <div className="section-header">
              <h2>{content.clubHistory.title}</h2>
            </div>
            <div className="content-body">
              {formatContent(content.clubHistory.content)}
            </div>
          </section>
        )}

        {/* Weather Widget */}
        <section className="weather-section">
          <h2>Live Wind at {FEATURED_STATION.name}</h2>
          <div className="weather-station-featured">
            <WeatherStation
              stationId={FEATURED_STATION.stationId}
              name={FEATURED_STATION.name}
              mode="detailed"
            />
          </div>

          <h2 className="weather-forecast-heading">Local Weather Forecast</h2>
          <div className="weather-widget">
            <iframe 
              width="100%" 
              height="450" 
              src="https://embed.windy.com/embed2.html?lat=55.459&lon=-3.032&zoom=10&level=surface&overlay=wind&menu=&message=true&marker=&calendar=12&pressure=true&type=map&location=coordinates&detail=true&detailLat=55.486&detailLon=-3.221&metricWind=km%2Fh&metricTemp=default&radarRange=-1" 
              frameBorder="0"
              title="Local Weather Conditions"
            />
          </div>
        </section>

        {/* Latest News - only shown when there's a real article */}
        {content?.news && (content.news.introtext || content.news.content) && (
          <section className="news-section">
            <div className="section-header">
              <h2>{content.news.title || 'Latest News'}</h2>
              {(content.news.created_date || content.news.updated_date) && (
                <span className="last-updated">
                  {formatDate(content.news.created_date || content.news.updated_date)}
                </span>
              )}
            </div>
            <div className="content-body">
              <p className="news-teaser">{newsTeaser(content.news)}</p>
              <p className="news-read-more">
                <a href={`/news/${content.news.news_id}`} className="news-read-more-btn">Read more →</a>
                <a href="/news" className="news-all-link">All news</a>
              </p>
            </div>
          </section>
        )}

        {/* Featured Sites */}
        {content?.featuredSites && content.featuredSites.length > 0 && (
          <section className="featured-sites">
            <h2>Featured Flying Sites</h2>
            <div className="sites-grid">
              {content.featuredSites.map((site, index) => (
                <a
                  key={site.site_id || index}
                  href={`/sites/${site.site_id}`}
                  className="site-card"
                >
                  <div className="site-card-head">
                    <h3>{site.name || 'Unnamed Site'}</h3>
                    {site.status && (
                      <span className={`site-status ${String(site.status).toLowerCase()}`}>
                        {site.status}
                      </span>
                    )}
                  </div>
                  <dl className="site-card-facts">
                    {site.direction && (
                      <div><dt>Wind</dt><dd>{site.direction}</dd></div>
                    )}
                    {(site.height_meters || site.height) && (
                      <div><dt>Height</dt><dd>{site.height_meters ? `${site.height_meters}m` : site.height}</dd></div>
                    )}
                  </dl>
                  <span className="site-card-link">View site guide →</span>
                </a>
              ))}
            </div>
            <p className="featured-sites-all">
              <a href="/sites">See all flying sites</a>
            </p>
          </section>
        )}

        {/* Recent Flight Logs */}
        <RecentFlightLogs logs={content?.recentFlightLogs} />

        {(!content?.clubHistory && !content?.news) && (
          <section className="no-content-message">
            <h2>Welcome to Wingbeat Paragliding Club</h2>
            <p>Content is being loaded. Please check back soon!</p>
          </section>
        )}
      </div>
    </ErrorBoundary>
  );
}

export default HomePage;
